import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import useHasPermission from '@/hooks/useHasPermission';
import { FEATURE_FLAGS } from '@/utils/featureFlags';
import { RESOURCES } from '@/utils/permissions';

export const useNavigationPermissions = (panelId?: string) => {
  const { hasPermission: hasPermissionToViewCompany } = useHasPermission({
    action: RESOURCES.company.actions.view,
    resource: RESOURCES.company.id,
  });

  const { hasPermission: hasPermissionToViewPanelist } = useHasPermission({
    action: RESOURCES.panelist.actions.view,
    resource: RESOURCES.panelist.id,
  });

  const { hasPermission: hasPermissionToViewBulkBan } = useHasPermission({
    action: RESOURCES.bulkban.actions.create,
    resource: RESOURCES.bulkban.id,
  });

  const { hasPermission: hasPermissionToViewBulkBalanceAdjust } =
    useHasPermission({
      action: RESOURCES.panelist.actions.ledger,
      resource: RESOURCES.panelist.id,
    });

  const { hasPermission: hasPermissionToViewEmailBlocklist } = useHasPermission(
    {
      action: RESOURCES.blocklist.actions.edit,
      resource: RESOURCES.blocklist.id,
    }
  );

  const { hasPermission: hasPermissionToViewBuyerIdBlocklist } =
    useHasPermission({
      action: RESOURCES.blocklist.actions.edit,
      resource: RESOURCES.blocklist.id,
    });

  const { hasPermission: hasPermissionToViewPaymentMethods } = useHasPermission(
    {
      action: RESOURCES.pm.actions.view,
      resource: RESOURCES.pm.id,
    }
  );

  const { hasPermission: hasPermissionToViewPanel } = useHasPermission({
    action: RESOURCES.panel.actions.view,
    resource: RESOURCES.panel.id,
    isAllCompaniesRequired: false,
  });

  const { hasPermission: hasPermissionToViewSignalTemplates } =
    useHasPermission({
      action: RESOURCES.marketing.actions.viewtmpl,
      resource: RESOURCES.marketing.id,
    });

  const { hasPermission: hasPermissionToViewTranslations } = useHasPermission({
    action: RESOURCES.translat.actions.view,
    resource: RESOURCES.translat.id,
  });

  const { hasPermission: hasPermissionToViewPaymentMethodCodes } =
    useHasPermission({
      action: [
        RESOURCES.pm.actions.viewcodes,
        RESOURCES.pm.actions.viewcocodes,
      ],
      resource: RESOURCES.pm.id,
    });

  const { hasPermission: hasPermissionToViewReports } = useHasPermission({
    action: RESOURCES.report.actions.view,
    resource: RESOURCES.report.id,
  });

  const { isFlagOn: isBuyerBlockingEnabled } = useFeatureFlag({
    name: FEATURE_FLAGS.IS_BUYER_BLOCKING_ENABLED,
  });

  const { isFlagOn: isAppCuesOn } = useFeatureFlag({
    name: FEATURE_FLAGS.IS_APPCUES_ON,
  });

  const { hasPermission: hasPermissionToViewTargetedSampling } =
    useHasPermission({
      action: RESOURCES.targetedSampling.actions.view,
      resource: RESOURCES.targetedSampling.id,
    });

  const { hasPermission: hasPermissionToPanelistImport } = useHasPermission({
    action: RESOURCES.panelist.actions.import,
    resource: RESOURCES.panelist.id,
  });

  const { hasPermission: hasPermissionToLeadImport } = useHasPermission({
    action: RESOURCES.lead.actions.batch,
    resource: RESOURCES.lead.id,
  });

  const { hasPermission: hasPermissionToManualActivation } = useHasPermission({
    action: RESOURCES.activation.actions.direct,
    resource: RESOURCES.activation.id,
  });

  const { hasPermission: hasPermissionToViewMarketingCampaigns } =
    useHasPermission({
      action: RESOURCES.marketing.actions.viewcamp,
      resource: RESOURCES.marketing.id,
    });

  const { hasPermission: hasPermissionToViewProfiling } = useHasPermission({
    action: RESOURCES.profiling.actions.view,
    resource: RESOURCES.profiling.id,
  });

  const { isFlagOn: isReferralProgramEnabled } = useFeatureFlag({
    name: FEATURE_FLAGS.IS_REFERRAL_PROGRAM_ENABLED,
    attributes: panelId ? { panel_id: panelId } : undefined,
  });

  return {
    hasPermissionToViewCompany,
    hasPermissionToViewPanelist,
    hasPermissionToViewBulkBan,
    hasPermissionToViewBulkBalanceAdjust,
    hasPermissionToViewEmailBlocklist,
    hasPermissionToViewBuyerIdBlocklist,
    hasPermissionToViewPaymentMethods,
    hasPermissionToViewPanel,
    hasPermissionToViewSignalTemplates,
    hasPermissionToViewTranslations,
    hasPermissionToViewPaymentMethodCodes,
    hasPermissionToViewReports,
    isBuyerBlockingEnabled,
    isAppCuesOn,
    hasPermissionToViewTargetedSampling,
    hasPermissionToPanelistImport,
    hasPermissionToLeadImport,
    hasPermissionToManualActivation,
    hasPermissionToViewMarketingCampaigns,
    hasPermissionToViewProfiling,
    isReferralProgramEnabled,
  };
};
